import { CloseIcon } from '@src/Assets/svg/CloseIcon';
import { PublishedIcon } from '@src/Assets/svg/PublishedIcon';
import { RightArrow } from '@src/Assets/svg/RightArrow';
import {
  closeReleaseBannerByUser,
  fetchReleaseBannerAckStatus,
  getFileAlertAcknowledgementSave,
  getReleaseNote,
  RELEASE_BANNER_STORAGE_KEYS,
} from '@src/apps/Header/Main/Main.service';
import React, { useEffect, useMemo, useState } from 'react';
import { BannerStyle } from './HeaderBanner.styles';

export const HeaderBanner: React.FC = () => {
  const [isExitProviderViewEnabled, setIsExitProviderViewEnabled] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissedForSession, setIsDismissedForSession] = useState(false);
  const [fullNotesFileId, setFullNotesFileId] = useState<string | undefined>();
  const [isFullNotesLaunchInProgress, setIsFullNotesLaunchInProgress] = useState(false);
  const handleExitProviderView = (event: Event) => {
    setIsExitProviderViewEnabled((event as CustomEvent<boolean>)?.detail);
  };

  useEffect(() => {
    window.addEventListener('ExitProviderView', handleExitProviderView);

    return () => {
      window.removeEventListener('ExitProviderView', handleExitProviderView);
    };
  }, []);

  useEffect(() => {
    const initializeBannerState = async () => {
      const manuallyClosedForSession =
        sessionStorage.getItem(RELEASE_BANNER_STORAGE_KEYS.closedByUser) === 'true';

      if (manuallyClosedForSession) {
        setIsDismissedForSession(true);
        setIsVisible(false);
        return;
      }
      const response = await fetchReleaseBannerAckStatus();
      const fullNotesAcknowledged = Boolean(response?.isFullNotesAcknowledged);
      setFullNotesFileId(response?.fullNotesFileId);
      setIsVisible(!fullNotesAcknowledged);
    };

    initializeBannerState();
  }, []);

  const onFullNotesClick = async () => {
    if (isFullNotesLaunchInProgress) {
      return;
    }

    setIsFullNotesLaunchInProgress(true);

    const flag = await getReleaseNote();
    if (!flag) {
      setIsFullNotesLaunchInProgress(false);
      return;
    }

    const isSaved = await getFileAlertAcknowledgementSave(
      fullNotesFileId,
      undefined,
      undefined,
      RELEASE_BANNER_STORAGE_KEYS.fullNotes,
      false
    );

    if (isSaved) {
      setIsVisible(false);
    }

    setIsFullNotesLaunchInProgress(false);
  };

  const closeReleaseBannerForCurrentSession = () => {
    closeReleaseBannerByUser();
    setIsDismissedForSession(true);
    setIsVisible(false);
  };

  const shouldShowBanner = useMemo(() => {
    const manualClosed =
      sessionStorage.getItem(RELEASE_BANNER_STORAGE_KEYS.closedByUser) === 'true';
    const isSimulationMode = sessionStorage.getItem('simulationMode') === 'true';

    if (isDismissedForSession) {
      return false;
    }
    return !manualClosed && !isSimulationMode && isVisible;
  }, [isDismissedForSession, isVisible]);

  const isBannerRendered = shouldShowBanner && !isExitProviderViewEnabled;

  return (
    <React.Fragment>
      {isBannerRendered && (
        <BannerStyle>
          <div className="release-text">
            <div className="release-notes-text">
              <div className="release-notes-text-left">
                <span className="release-icon">
                  <PublishedIcon />
                </span>{' '}
                Discover the newest features, enhancements and updates.{` `}
                <a
                  onClick={onFullNotesClick}
                  className="view-release"
                  style={{
                    pointerEvents: isFullNotesLaunchInProgress ? 'none' : 'auto',
                    opacity: isFullNotesLaunchInProgress ? 0.6 : 1,
                  }}
                >
                  Read Release Notes{' '}
                  <span className="icon-align">
                    <RightArrow />
                  </span>
                </a>
              </div>
              <div className="release-notes-text-right">
                <div>
                  <button
                    type="button"
                    aria-label="Close release banner"
                    data-testid="close-icon"
                    className="close-icon"
                    onClick={closeReleaseBannerForCurrentSession}
                  >
                    <CloseIcon />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </BannerStyle>
      )}
    </React.Fragment>
  );
};
